import { OrderInterface, OrderVariantInterface, SidePanelInterface } from '../types';
import SidePanel from './SidePanel';
import React, { useEffect, useState } from 'react';
import OrderDetails from './orders/OrderDetails';
import EditOrderForm from './orders/EditOrderForm';
import { CreateButton } from './SharedComponents';
import apiClient from '../services/apiClient';
import { Checkbox, CircularProgress, FormControlLabel, FormGroup } from '@mui/material';
import notify from '../services/notify';
import ReactGA from 'react-ga4';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { ArrowLeftIcon } from '@mui/x-date-pickers';
import ConfirmationModal from './ConfirmationModal';

interface OrderModalProps extends Omit<SidePanelInterface, 'children'> {
	order?: OrderInterface | null;
	handleHidePanel: (order?: OrderInterface | null) => void;
}

const fetchOrderVariants = (orderID: number) => {
	return apiClient.get(`/orders/${orderID}/variants`);
};

const getInvoiceViewUrl = (order: any) => {
	// Use the frontend route with the hash_id as a query parameter
	if (!order || !order.hash_id) {
		console.error('Order or hash_id is missing', order);
		return '';
	}
	return `/invoice?hash=${order.hash_id}&skipTracking=true`;
};

export default function OrderModal(props: OrderModalProps) {
	const { order, handleHidePanel, ...sidePanelProps } = props;

	const [isOrderPaymentStatusLoading, setIsOrderPaymentStatusLoading] = useState<boolean>(false);
	const [variants, setVariants] = useState<OrderVariantInterface[]>([]);
	const [totalOrderPrice, setTotalOrderPrice] = useState<number | null>(null);
	const [isOrderVariantsLoading, setIsOrderVariantsLoading] = useState<boolean>(false);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [isOrderPaid, setIsOrderPaid] = useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);

	// Effect to determine if we should be in edit mode based on order
	useEffect(() => {
		if (order) {
			if (order.id) {
				// Existing order - start in view mode
				setIsOrderPaid(order.is_order_paid);
				setIsEditing(false);
				ReactGA.event({
					category: 'Order',
					action: 'Viewed Order',
					label: `Order ID: ${order.id}`,
				});
			} else {
				// New order (no id) - start in edit mode
				setIsEditing(true);
			}
		}
	}, [order]);

	useEffect(() => {
		if (order && order.id) {
			setIsOrderVariantsLoading(true);
			fetchOrderVariants(order.id)
				.then((response: any) => {
					setIsOrderVariantsLoading(false);
					setVariants(response.data.variants);
					setTotalOrderPrice(response.data.order_price_total);
				})
				.catch((error: any) => {
					setIsOrderVariantsLoading(false);
					notify('error', 'Error fetching order variants');
					console.error('Error fetching order variants', error);
				});
		} else {
			// For new orders, we want to start with an empty variant list
			setVariants([]);
		}
	}, [order]);

	const handleOrderPaymentStatus = () => {
		setIsOrderPaymentStatusLoading(true);
		if (order) {
			apiClient
				.update(`/orders/update-payment-status/${order.id}`, { is_paid: !isOrderPaid })
				.then((response: any) => {
					setIsOrderPaid(!isOrderPaid);
					notify('info', 'Payment status updated');
					hidePanel({
						...order,
						is_order_paid: !isOrderPaid,
					});
					console.log('payment status updated', response.data);
					ReactGA.event({
						category: 'Order',
						action: 'Updated Payment Status',
						label: `Order ID: ${order.id}`,
						value: !isOrderPaid ? 1 : 0, // 1 for paid, 0 for unpaid
					});
				})
				.catch((error: any) => {
					notify('error', 'Error updating payment status');
					console.error('Error updating payment status', error);
					ReactGA.event({
						category: 'Order',
						action: 'Failed to Update Payment Status',
						label: `Order ID: ${order.id}`,
					});
				})
				.finally(() => {
					setIsOrderPaymentStatusLoading(false);
				});
		}
	};

	const viewInvoice = (order?: any) => {
		if (!order || !order.hash_id) return;

		// Open invoice view in new tab
		window.open(getInvoiceViewUrl(order), '_blank');

		ReactGA.event({
			category: 'Order',
			action: 'Viewed Invoice',
			label: `Invoice Hash: ${order.hash_id}`,
		});
	};

	const hidePanel = (order?: OrderInterface | null) => {
		setVariants([]);
		setTotalOrderPrice(null);
		setIsEditing(false);
		if (order) {
			handleHidePanel(order);
		} else if (order === null) {
			// Explicitly handle deletion case
			handleHidePanel(null);
		} else {
			// No order passed (undefined) - just close modal without changes
			handleHidePanel();
		}
		ReactGA.event({
			category: 'User Interaction',
			action: 'Closed Order Panel',
			label: order ? `Order ID: ${order.id}` : 'No Order',
		});
	};

	const handleEditOrderClick = () => {
		ReactGA.event({
			category: 'User Interaction',
			action: 'Edit Order',
			label: order ? `Order ID: ${order.id}` : 'No Order',
		});

		setIsEditing(true);
	};

	const handleDeleteOrder = async () => {
		if (!order || !order.id) return;

		setIsDeleting(true);
		try {
			await apiClient.delete(`/orders/${order.id}`);
			notify('success', 'Order deleted successfully');

			ReactGA.event({
				category: 'Order',
				action: 'Deleted Order',
				label: `Order ID: ${order.id}`,
			});

			// Close the modal and notify parent to remove from list
			setIsDeleteDialogOpen(false);
			handleHidePanel(null); // Pass null to indicate deletion
		} catch (error) {
			console.error('Error deleting order:', error);
			notify('error', 'Failed to delete order');

			ReactGA.event({
				category: 'Order',
				action: 'Failed to Delete Order',
				label: `Order ID: ${order.id}`,
			});
		} finally {
			setIsDeleting(false);
		}
	};

	const headerComponents: any[] = [];

	if (!isEditing) {
		headerComponents.push(
			<CreateButton key="edit-button" onClick={handleEditOrderClick} outline>
				<ModeEditOutlinedIcon style={{ fontSize: '17px' }} />
				<span>Edit Order</span>
			</CreateButton>
		);
	}

	if (order && !isEditing) {
		headerComponents.push(
			<CreateButton key="view-invoice-button" onClick={() => viewInvoice(order)}>
				{/* <ModeEditOutlinedIcon style={{ fontSize: '17px' }} /> */}
				<span>View Invoice</span>
			</CreateButton>
		);
	}

	if (isEditing && order && order.id) {
		headerComponents.push(
			<CreateButton key="download-invoice-button" onClick={() => setIsEditing(false)} outline>
				<ArrowLeftIcon style={{ fontSize: '17px' }} />
				<span>Cancel Edit</span>
			</CreateButton>
		);
	}

	return (
		<SidePanel {...sidePanelProps} handleHidePanel={() => hidePanel()} headerComponents={headerComponents}>
			{isEditing ? (
				<EditOrderForm
					order={order}
					variants={variants}
					handleHidePanel={hidePanel}
					isOrderVariantsLoading={isOrderVariantsLoading}
					cancelEdit={() => setIsEditing(false)}
					onDeleteClick={() => setIsDeleteDialogOpen(true)}
				/>
			) : (
				<>
					<OrderDetails
						order={order}
						isOrderVariantsLoading={isOrderVariantsLoading}
						variants={variants}
						totalOrderPrice={totalOrderPrice}
						onOrderUpdate={hidePanel}
					/>
					<FormGroup style={{ paddingTop: '20px' }}>
						<FormControlLabel
							control={
								isOrderPaymentStatusLoading ? (
									<CircularProgress size={20} />
								) : (
									<Checkbox
										checked={isOrderPaid}
										onChange={() => handleOrderPaymentStatus()}
										color="primary"
									/>
								)
							}
							label="Mark Order As Paid"
						/>
					</FormGroup>
				</>
			)}

			<ConfirmationModal
				isOpen={isDeleteDialogOpen}
				title="Delete Order"
				message={`Are you sure you want to delete this order${
					order?.customer_name ? ` for ${order.customer_name}` : ''
				}? This action cannot be undone.`}
				confirmText="Delete Order"
				cancelText="Cancel"
				onConfirm={handleDeleteOrder}
				onCancel={() => setIsDeleteDialogOpen(false)}
				isLoading={isDeleting}
				variant="danger"
			/>
		</SidePanel>
	);
}
